import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { forkJoin, from, map, Observable, of, Subject, switchMap } from 'rxjs';

import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import {
  DisplaySummary,
  DisplaySummaryService,
} from 'src/app/displays/services/display-summary.service';
import { PlayerLicenseService } from 'src/app/displays/services/player-license.service';

@Component({
  selector: 'display-license-counts',
  templateUrl: './display-license-counts.component.html',
  styleUrls: ['./display-license-counts.component.scss'],
  standalone: false,
})
export class DisplayLicenseCountsComponent implements OnInit, OnChanges {
  @Input() showOnlineCount: boolean;
  @Input() countOnly: boolean;
  @Input() includeSubcompanies: boolean = false;

  // Does not emit until ngOnInit, so the first request uses the bound
  // includeSubcompanies value instead of the default set at construction.
  private licenseCountsSubject = new Subject<void>();
  protected items = toSignal(
    this.licenseCountsSubject
      .asObservable()
      .pipe(switchMap(() => this.getDisplayCounts())),
  );

  get requiredLicenses(): number {
    let required = 0;
    if (
      this.displaySummaryService.summary &&
      this.displaySummaryService.summary.scheduledUnlicensed
    ) {
      required =
        this.displaySummaryService.summary.scheduledUnlicensed -
        this.playerLicenseService.getProAvailableLicenseCount();
    }
    return required;
  }

  constructor(
    public playerLicenseService: PlayerLicenseService,
    public currentPlanService: CurrentPlanService,
    public displaySummaryService: DisplaySummaryService,
    public featuresService: FeaturesService,
  ) {}

  ngOnInit(): void {
    this.licenseCountsSubject.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['includeSubcompanies'] &&
      !changes['includeSubcompanies'].firstChange
    ) {
      this.licenseCountsSubject.next();
    }
  }

  purchaseLicenses() {
    if (this.requiredLicenses) {
      this.currentPlanService.showPurchaseOptions(this.requiredLicenses);
    }
  }

  private getDisplayCounts(): Observable<DisplayCounts> {
    return forkJoin([
      this.getDisplaySummary(this.displaySummaryService),
      this.getDisplayLicenses(
        this.playerLicenseService.getProUsedLicenseCount(),
        this.currentPlanService.isUnlimitedPlan(),
        this.playerLicenseService.getProAvailableLicenseCount(),
        this.playerLicenseService.hasProfessionalLicenses(),
      ),
      this.getInteractiveLicenses(),
    ]).pipe(map(this.buildCounts));
  }

  private getInteractiveLicenses() {
    return of(
      this.playerLicenseService.hasInteractiveLicenses()
        ? {
            used: `${this.playerLicenseService.getInteractiveUsedLicenseCount()} Licensed`,
            available: `${this.playerLicenseService.getInteractiveAvailableLicenseCount()} Available`,
          }
        : null,
    );
  }

  private getDisplayLicenses(
    proUsedLicenseCount: number,
    isUnlimitedPlan: boolean,
    proAvailableLicenseCount: number,
    hasProfessionalLicenses: boolean,
  ): Observable<{ used: number; available: string }> {
    return of({
      used: proUsedLicenseCount,
      available: isUnlimitedPlan
        ? hasProfessionalLicenses
          ? 'Unlimited'
          : ''
        : proAvailableLicenseCount + ' Available',
    });
  }

  private getDisplaySummary(
    service: DisplaySummaryService,
  ): Observable<DisplaySummary> {
    return from(service.loadSummary(this.includeSubcompanies)).pipe(
      map(() => service.summary),
    );
  }

  private buildCounts([summary, licenses, interactiveLicenses]: [
    DisplaySummary,
    { used: number; available: string },
    { used: string; available: string } | null,
  ]): DisplayCounts {
    const counts: DisplayCounts = [
      {
        icon: 'display',
        color: 'blue',
        text: `${licenses.used} Licensed`,
        subtext: licenses.available,
        tooltip: 'Digital Signage',
      },
    ];

    if (interactiveLicenses) {
      counts.push({
        icon: 'cursor-hand',
        color: 'purple',
        text: interactiveLicenses.used,
        subtext: interactiveLicenses.available,
        tooltip: 'Interactive',
      });
    }

    counts.push({
      icon: 'wifi',
      color: 'green',
      text: `${summary.online} Online`,
      subtext: `${summary.offline} Offline`,
      tooltip: 'Online',
    });

    if (summary.scheduledUnlicensed) {
      counts.push({
        icon: 'alert-triangle',
        color: 'amber',
        text: `${summary.scheduledUnlicensed} Scheduled`,
        subtext: 'Unlicensed',
        tooltip: 'Scheduled Unlicensed',
      });
    }

    return counts;
  }
}

type DisplayCounts = Array<{
  icon: string;
  color: 'blue' | 'purple' | 'green' | 'amber';
  text: string;
  subtext: string;
  tooltip: string;
}>;
